"use strict";
// packages/rdti-schema/src/compliance.ts
//
// Schema-driven compliance scoring. Replaces the FY24-only hardcoded scorer
// in apps/client. Only narrative fields with a `complianceRules` entry get a
// rich score; structured fields (radio / dropdown / currency / etc.) get a
// binary required-or-not score under `isStructured`.
Object.defineProperty(exports, "__esModule", { value: true });
exports.scoreField = scoreField;
exports.scoreSection = scoreSection;
const registry_1 = require("./registry");
function isFieldVisible(field, responses) {
    if (!field.conditionalOn)
        return true;
    const actual = responses[field.conditionalOn.field];
    const expected = field.conditionalOn.equals;
    if (Array.isArray(expected)) {
        return expected.includes(String(actual));
    }
    if (Array.isArray(actual)) {
        return actual.map(String).includes(expected);
    }
    return String(actual) === expected;
}
function isFilled(value) {
    if (value === null || value === undefined)
        return false;
    if (typeof value === 'string')
        return value.trim().length > 0;
    if (Array.isArray(value))
        return value.length > 0;
    if (typeof value === 'object')
        return Object.keys(value).length > 0;
    return true;
}
function scoreField(field, value) {
    const filled = isFilled(value);
    if (!field.complianceRules) {
        return {
            fieldKey: field.key,
            score: field.required ? (filled ? 100 : 0) : 100,
            isStructured: true,
            issues: field.required && !filled ? ['Required field is empty'] : [],
            suggestions: [],
            hasRedFlags: false,
            redFlags: [],
        };
    }
    const text = typeof value === 'string' ? value.trim() : '';
    const rules = field.complianceRules;
    if (text.length === 0) {
        return {
            fieldKey: field.key,
            score: 0,
            isStructured: false,
            issues: ['Field is empty'],
            suggestions: ['This field is important for AusIndustry compliance'],
            hasRedFlags: false,
            redFlags: [],
        };
    }
    let score = 100;
    const issues = [];
    const suggestions = [];
    const redFlags = [];
    if (field.minChars && text.length < field.minChars) {
        score -= 30;
        issues.push(`Below AusIndustry minimum (${text.length}/${field.minChars} characters)`);
        suggestions.push('Submission will be blocked until this minimum is reached');
    }
    else if (field.maxChars && text.length < Math.min(field.maxChars * 0.1, 100)) {
        score -= 25;
        issues.push('Too brief for an audit-defensible answer');
        suggestions.push('Add more technical detail and specificity');
    }
    const lower = text.toLowerCase();
    const hasKeyword = rules.keywords.some((kw) => lower.includes(kw.toLowerCase()));
    if (!hasKeyword) {
        score -= 20;
        issues.push('Missing key compliance terminology');
        suggestions.push(`Consider including terms like: ${rules.keywords.slice(0, 3).join(', ')}`);
    }
    rules.redFlags.forEach((flag) => {
        if (lower.includes(flag.toLowerCase())) {
            score -= 15;
            redFlags.push(flag);
            issues.push(`Contains non-R&D language: "${flag}"`);
            suggestions.push(`Avoid phrases like "${flag}" — they suggest routine work, not R&D`);
        }
    });
    const vagueTerms = ['improve', 'better', 'faster', 'optimize'];
    vagueTerms.forEach((term) => {
        if (lower.includes(term) && !lower.includes('specific') && text.length < 200) {
            score -= 5;
            suggestions.push(`Be more specific about "${term}" — include metrics or technical details`);
        }
    });
    score = Math.max(0, Math.min(100, score));
    return {
        fieldKey: field.key,
        score,
        isStructured: false,
        issues,
        suggestions,
        hasRedFlags: redFlags.length > 0,
        redFlags,
    };
}
function scoreSection(version, section, responses) {
    const fields = (0, registry_1.getFieldsForSection)(version, section);
    const visible = fields.filter((f) => isFieldVisible(f, responses));
    const fieldScores = {};
    let weightedScore = 0;
    let totalWeight = 0;
    let filled = 0;
    const criticalIssues = [];
    for (const field of visible) {
        const value = responses[field.key];
        const result = scoreField(field, value);
        fieldScores[field.key] = result;
        if (isFilled(value))
            filled++;
        if (field.complianceRules) {
            const weight = field.complianceRules.weight;
            weightedScore += result.score * weight;
            totalWeight += weight;
            if (result.hasRedFlags && weight >= 0.15) {
                criticalIssues.push(`${field.label}: ${result.redFlags.join(', ')}`);
            }
        }
    }
    return {
        overallScore: totalWeight > 0 ? Math.round(weightedScore / totalWeight) : 0,
        completeness: visible.length > 0 ? Math.round((filled / visible.length) * 100) : 0,
        fieldScores,
        criticalIssues,
    };
}
